import { Box, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import {
  FONT_BILLY_BOLD,
  FONT_GSS_FUTURA_SEMI_BOLD,
} from '@monorepo/shared/src/themes/studentAppTheme';
import { loadAudioAsync } from '@monorepo/shared/src/util/activity-audio-util';
import useRewardsStore from 'graph/state/query-hooks/use-rewards-store';
import MainLayout from 'components/layout/MainLayout';
import WithQuery from 'components/layout/WithQuery';
import Sticker from './components/Sticker';
import type {
  Maybe,
  StudentReward,
} from '@monorepo/shared/src/graph/schema-generated-types-student';
import { ButtonTypes } from 'types';
import { getNewRewardData } from 'util/rewards-util';
import useAffirmationsStore from 'graph/state/query-hooks/use-affirmations-store';
import React, { useEffect } from 'react';

type NewStickerProps = {
  character?: string;
  onClose: () => void;
  isActivityAffirmation?: boolean;
  studentReward?: Maybe<StudentReward> | undefined;
};

const NewSticker: React.FC<NewStickerProps> = ({
  character,
  onClose,
  isActivityAffirmation,
  studentReward,
}) => {
  const classes = styles();
  const [{ charactersRewards, loading, error }] = useRewardsStore();
  const [{ affirmations, loading: affirmationsLoading, error: affirmationsError }] =
    useAffirmationsStore({ count: 1, randomize: true });

  const characterRewards = charactersRewards.find(
    (reward) => reward?.character.character === character
  );
  const selectedSticker = isActivityAffirmation
    ? studentReward
    : characterRewards?.studentRewards?.find((reward) => reward.selected);

  const rewardIndex = characterRewards?.studentRewards?.length || 1;

  const newRewardData = character && getNewRewardData(character)[rewardIndex];

  const invalidData = !loading && !newRewardData;

  if (invalidData) {
    onClose();
  }

  if (!newRewardData) {
    return <></>;
  }

  useEffect(() => {
    const loadAndPlay = async () => {
      const rewardSound =
        selectedSticker?.reward?.audio?.src &&
        (await loadAudioAsync(selectedSticker?.reward?.audio));
      if (!affirmations?.[0]) {
        await rewardSound.play();
        return;
      } else {
        const randomAffirmation = affirmations[0];
        const affirmationSound = await loadAudioAsync(randomAffirmation, {
          onend: async () => {
            rewardSound && (await rewardSound.play());
          },
        });
        affirmationSound.play();
      }
    };
    if (!loading && !affirmationsLoading) {
      loadAndPlay();
    }
  }, [loading, affirmationsLoading]);

  return (
    <MainLayout
      backgroundImg={newRewardData.backgroundImage}
      menuProps={{
        ...(!isActivityAffirmation && {
          primaryRightButton: {
            name: ButtonTypes.CLOSE_BUTTON,
            onClick: onClose,
          },
        }),
      }}
    >
      <WithQuery loading={loading || affirmationsLoading} error={error || affirmationsError}>
        <Box className={classes.root}>
          <img src={newRewardData.matManImageSrc} alt="" />
          <Box className={classes.rewardContainer}>
            <Typography className={classes.title}>{affirmations?.[0]?.description}</Typography>
            {selectedSticker && <Sticker studentReward={selectedSticker} />}
            {selectedSticker && (
              <Typography className={classes.summary}>{newRewardData.summary}</Typography>
            )}
          </Box>
        </Box>
      </WithQuery>
    </MainLayout>
  );
};

const styles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    gridGap: theme.typography.pxToRem(64),
    //TODO: Define app breakpoints
    [theme.breakpoints.down(800)]: {
      flexDirection: 'column',
    },
  },
  rewardContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gridGap: theme.typography.pxToRem(35),
  },
  title: {
    fontSize: theme.typography.pxToRem(80),
    lineHeight: theme.typography.pxToRem(95),
    fontFamily: FONT_BILLY_BOLD,
  },
  summary: {
    fontSize: theme.typography.pxToRem(42),
    lineHeight: theme.typography.pxToRem(48),
    fontFamily: FONT_GSS_FUTURA_SEMI_BOLD,
    maxWidth: theme.typography.pxToRem(536),
    textAlign: 'center',
  },
}));

export default NewSticker;
